/**
 * CustomRefinementList component is a customizable filter list that integrates with Algolia's InstantSearch library.
 * It allows users to refine product search results by selecting specific attribute values (facets) from a list of options.
 *
 * Features:
 * - Displays a list of items (e.g., attributes like color, size, etc.) with checkboxes for refining search results.
 * - Supports transforming the list items through the `transformItems` prop for custom formatting.
 * - Conditionally renders a "Show More" button to toggle additional facet options if the number of items exceeds the limit.
 * - Items are rendered with counts to indicate how many results are associated with each option.
 * - Supports pagination for the facet list via the `limit` and `showMoreLimit` props.
 *
 * @param {string} attribute - The facet attribute to be refined (e.g., "colour", "size").
 * @param {function} transformItems - Optional function to transform or filter facet items before rendering.
 * @param {boolean} showMore - Whether to show the "Show More" button to expand the list (default is true).
 * @param {number} limit - The maximum number of items to display initially (default is 10).
 * @param {number} showMoreLimit - The maximum number of items to display when expanded (default is 1000).
 * @returns {JSX.Element|null} - A refined list of checkboxes for the selected attribute, or null if no refinements are available.
 */
import React from 'react';
import { useRefinementList, UseRefinementListProps } from 'react-instantsearch-hooks';

interface Props extends UseRefinementListProps {
  transformItems?: (items: any[]) => any[];
  showMore?: boolean;
  limit?: number;
  showMoreLimit?: number;
}

function CustomRefinementList({ attribute, transformItems, showMore = true, limit = 10, showMoreLimit = 1000 }: Props) {
  const { items, refine, searchForItems, canRefine, canToggleShowMore, isShowingMore, toggleShowMore } =
    useRefinementList({
      attribute,
      limit,
      showMoreLimit,
      showMore,
      transformItems,
    });

  if (!canRefine) return null;

  return (
    <div>
      <ul className="space-y-2">
        {items.map((item) => (
          <li key={item.label} className="flex w-full items-center">
            <label className="flex w-full items-center justify-between">
              <input
                type="checkbox"
                checked={item.isRefined}
                onChange={() => refine(item.value)}
                className="mr-8 mt-1 h-19 w-19 rounded-sm border border-gray-300 bg-white shadow-inner focus:outline-none"
              />
              <span className="flex-grow text-16 hover:text-[#127E57]">{item.label}</span>
              <span className="text-16 text-gray-500 before:content-['('] after:content-[')']">{item.count}</span>
            </label>
          </li>
        ))}
      </ul>

      {showMore && canToggleShowMore && (
        <button onClick={toggleShowMore} className="mt-10 block w-full text-center text-16 hover:text-[#127E57]">
          {isShowingMore ? '- Collapse' : '+ Show more'}
        </button>
      )}
    </div>
  );
}

export default CustomRefinementList;
